import { ReactNode, useEffect, useEffectEvent, useState } from 'react'

import { AstraAlertDialog } from '~/astra'
import { useStore } from '~/store/store'
import { useDateRangeDates, usePractitionerStatusDefinitions } from '~/storm'
import { isDepartmentIncluded } from '~/utils/surgeryActivities'

import { DatePractitionerCell } from '../../shared'
import { toggleStatus } from '../Modal/Statuses/toggleStatus'
import { copySelectedCells, cutSelectedCells, deleteSelectedActivities, pasteClipboardAtCell } from './keyboardActions'

type Props = {
    selectedCells: DatePractitionerCell[]
    blockKeyInput: boolean
}

type ConfirmOptions = {
    title: string
    description: ReactNode
    cancelButtonLabel: string
    confirmButtonLabel: string
}

type Confirmation = ConfirmOptions & {
    resolve: (confirmed: boolean) => void
}

type PractitionerStatusDefinition = ReturnType<typeof usePractitionerStatusDefinitions>[number]

function matchesShortcut(status: PractitionerStatusDefinition, keyCode: string, isMetaKeyPressed: boolean, isShiftKeyPressed: boolean) {
    const shortcut = status.keyboard_shortcut
    if (!shortcut) return false
    if ((status.keyboard_has_meta_key ?? false) !== isMetaKeyPressed) return false
    if ((status.keyboard_has_shift ?? false) !== isShiftKeyPressed) return false

    // Letters are reported as KeyA, KeyB, etc. Special keys use their code directly, such as Equal or Minus.
    return `Key${shortcut}` === keyCode || shortcut === keyCode
}

export const KeyboardInteraction = ({ selectedCells, blockKeyInput }: Props) => {
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
    const departmentKey = useStore(state => state.appFilters.departmentKey)
    const visibleDates = useDateRangeDates()
    const statusDefinitions = usePractitionerStatusDefinitions({ include: { departments: true } })

    function confirm(options: ConfirmOptions) {
        return new Promise<boolean>(resolve => {
            setConfirmation({ ...options, resolve })
        })
    }

    function closeConfirmation(confirmed: boolean) {
        const resolve = confirmation?.resolve
        setConfirmation(null)
        resolve?.(confirmed)
    }

    const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
        const firstCell = selectedCells.at(0)
        if (!firstCell) return

        // Prevent shortcuts while another modal owns keyboard focus.
        if (blockKeyInput || confirmation) return

        const keyCode = e.code
        const isMetaKeyPressed = e.metaKey || e.ctrlKey
        const isShiftKeyPressed = e.shiftKey

        if (keyCode === 'Backspace' || keyCode === 'Delete') {
            void deleteSelectedActivities(selectedCells, confirm).catch(console.error)
            return
        }

        if (isMetaKeyPressed && keyCode === 'KeyC') {
            copySelectedCells(selectedCells)
            return
        }

        if (isMetaKeyPressed && keyCode === 'KeyX') {
            cutSelectedCells(selectedCells)
            return
        }

        if (isMetaKeyPressed && keyCode === 'KeyV') {
            void pasteClipboardAtCell(firstCell, visibleDates, confirm).catch(console.error)
            return
        }

        for (const status of statusDefinitions) {
            if (!isDepartmentIncluded(departmentKey, status.departments)) continue
            if (!matchesShortcut(status, keyCode, isMetaKeyPressed, isShiftKeyPressed)) continue

            void toggleStatus(selectedCells, status.code).catch(console.error)
            return
        }
    })

    useEffect(() => {
        function listener(e: KeyboardEvent) {
            handleKeyDown(e)
        }

        document.addEventListener('keydown', listener)

        return () => {
            document.removeEventListener('keydown', listener)
        }
    }, [])

    return (
        <AstraAlertDialog
            isOpen={confirmation !== null}
            title={confirmation?.title ?? ''}
            description={confirmation?.description ?? ''}
            confirmButtonLabel={confirmation?.confirmButtonLabel ?? ''}
            cancelButtonLabel={confirmation?.cancelButtonLabel ?? ''}
            onConfirm={() => closeConfirmation(true)}
            onCancel={() => closeConfirmation(false)}
        />
    )
}
